import styled from "@emotion/styled"
import { FloatingArrow, FloatingPortal, useMergeRefs } from "@floating-ui/react"
import {
	radiusMd,
	colorCanvas,
	shadowSm,
	durationSm,
	spacingStaticMd,
	proseTextSmStyle,
} from "@porsche-design-system/components-react/emotion"
import { useEffect, useMemo, useRef } from "react"
import type { PropsWithChildren, Ref } from "react"
import { useTooltipContext } from "./useTooltipContext"

interface TooltipContentProps extends PropsWithChildren {
	appendToBody?: boolean
	ref?: Ref<HTMLDivElement>
}

export const TooltipContent = ({ appendToBody, ref: propRef, ...props }: TooltipContentProps) => {
	const context = useTooltipContext()
	const popoverRef = useRef<HTMLDivElement>(null)
	const ref = useMergeRefs([context.floating.refs.setFloating, propRef, popoverRef])

	const floatingProps = useMemo(() => context.interactions.getFloatingProps(props), [context, props])

	const { arrowRef, floating, open: isOpen, transitionStatus } = context
	const { context: floatingContext, floatingStyles, placement } = floating
	const { status } = transitionStatus

	useEffect(() => {
		// `popover="manual"` promotes this element to its own #top-layer entry - independent of its
		// DOM position - stacked above any ancestor <dialog>/popover (e.g. PModal, PFlyout) it is
		// logically nested within, and escaping any ancestor's `overflow`/`clip-path`. Only relevant
		// when rendered inline (not `appendToBody`): `appendToBody` already portals to `document.body`
		// to escape clipping ancestors, and combining that with `popover` breaks positioning.
		//
		// Depends on `isOpen` (rather than running once on mount) because `TooltipContent` itself is
		// mounted as soon as there's `content` - the `<Content>` div (and `popoverRef.current`) only
		// exists once `isOpen` becomes true, so the ref wouldn't be attached yet on initial mount.
		const element = popoverRef.current
		// feature-detect: the Popover API isn't implemented by jsdom (used in unit tests), and this
		// guards production against any browser that doesn't support it either
		if (appendToBody || !isOpen || typeof element?.showPopover !== "function") {
			return
		}

		element.showPopover()

		return () => {
			// guard against calling hidePopover on a node React has already removed from the document
			if (element.isConnected) {
				element.hidePopover()
			}
		}
	}, [appendToBody, isOpen])

	const content = useMemo(
		() => (
			<Content
				ref={ref}
				data-placement={placement}
				data-status={status}
				popover={appendToBody ? undefined : "manual"}
				// eslint-disable-next-line no-restricted-syntax
				style={{ ...floatingStyles }}
			>
				<div {...floatingProps} />
				<FloatingArrow ref={arrowRef} fill={colorCanvas} context={floatingContext} />
			</Content>
		),
		[arrowRef, floatingContext, floatingStyles, placement, status, floatingProps, ref, appendToBody],
	)

	return isOpen ? appendToBody ? <FloatingPortal root={document.body}>{content}</FloatingPortal> : content : null
}

const Content = styled.div`
	padding: ${spacingStaticMd};
	z-index: 600;
	
	background: ${colorCanvas};
	font: ${proseTextSmStyle.font};
	
	box-sizing: border-box;
	border-radius: ${radiusMd};
	outline: 0;
	box-shadow: ${shadowSm};
	
	width: max-content;
	max-width: 650px;
	white-space: wrap;
	
	transition-property: opacity;
	
	&[data-status='open'],
	&[data-status='close'] {
		transition-duration: ${durationSm};
	}
	
	&[data-status='initial'],
	&[data-status="close"] {
		opacity: 0;
	}

	/* the UA stylesheet applies "inset: 0; margin: auto; border: solid; overflow: auto" to elements
	   with the [popover] attribute (its default centering/appearance). floating-ui's inline style
	   only overrides "top"/"left", so the leftover "right"/"bottom: 0" + "margin: auto" would
	   otherwise fight with our explicit position and snap the tooltip into a corner, and the default
	   border would show as a stray black outline since we don't otherwise set a border ourselves */
	&[popover] {
		inset: auto;
		margin: 0;
		border: none;
		overflow: visible;
	}
`
